import React, { Component } from 'react';
import { Header, List, Table } from 'semantic-ui-react';

export default class FinalPaperPage extends Component {
  render() {
    return (
      <div>
        <Header as="h1" content="Final paper" />
        <p>
          <b>Due Date:</b>
          4/26/22
        </p>

        <p>
          In addition to the presentation, you will present your findings in a final paper. This paper should be written in <a
          href="https://chi2019.acm.org/authors/chi-proceedings-format/" target="_blank">CHI 2019 Extended Abstracts</a> format.
        </p>

        <p>
          <b>Page limit</b>: Final papers should be up to 10 pages long. A good approach to writing a great short paper is to write a long
          one first, and then trim it down to the most vital parts. Appendices are acceptable and optional (they don't count towards
          the page limit), but won't be graded. Add one for materials you want an interested reader to see (for example, when I
          post your project on the website for next year), but don't need to be graded. The page limit includes references.
        </p>

        <p>
          Much of the advice from above for preparing your presentation applies to the paper as well. Here are a few more suggestions
          for preparing your paper:
        </p>

        <List bulleted>
          <List.Item>
            <a href="https://medium.com/gt-ups-course/resources-for-academic-writing-in-hci-82a27249cb5f" target="_blank">
              I've compiled a list of academic writing resources that you should review.
            </a>
          </List.Item>
          <List.Item>
            Find a paper that you particularly like because of how it's written, and use it as a template. This paper
            needn't be on the same topic, but a close mapping in terms of type of contribution (e.g. a tool paper vs. a
            theory paper) will give you more guidance as to how to structure your paper.
          </List.Item>
          <List.Item>
            The title and abstract are often the most important parts of a paper, and should clearly convey what you did.
            Motivate your specific problem (not the field as a whole), and focus on what you did. After reading the abstract,
            the reader should know what your contribution is – don't speak in generalities. For example, instead of saying
            "We evaluate a new method for preventing online behavior tracking by running a user study.", say "We present
            Stall-man, a new browser extension that prevents online behavior tracking while retaining webpage functionality by
            dynamically executing javascript in a sandboxed emulator that runs requests through Tor, awaits server-side
            results, and then copies over the resultant static HTML to the end-user's browser. In a between-subjects field
            experiment with 30 participants, we found that while Stall-man was noticeably slower than standard web browsing,
            participants preferred browsing with Stall-man at a 2:1 ratio because of the privacy benefits it provided."
          </List.Item>
          <List.Item>
            Review the Project proposal assignment. Make sure you clearly address each of the important bullets from the proposal in your final paper.
          </List.Item>
          <List.Item>
            Please use the APA heading structure to describe your study & results. Clearly tie your analysis to your hypotheses.
          </List.Item>
          <List.Item>
            Use pictures to show your interface and graphs to present your data. Graphs should generally aggregate across
            participants, and show standard error bars. (Only show individual data points if the reader learns something more
            by doing so.) Figures should be captioned with what you believe the reader should infer from the figure (e.g.
            Participants who used Stall-man were 60% more likely to feel that their privacy had not been compromised after
            a two-week study than those who did not).
          </List.Item>
        </List>

        <p>
          Here are two examples of excellent final papers from the first time I taught this class:
          <List bulleted>
            <List.Item><a href="/files/dynamic_audience_selection.pdf" target="_blank">Dynamic audience selection</a></List.Item>
            <List.Item><a href="/files/usable_audio_captchas.pdf" target="_blank">Usable audio CAPTCHAs</a></List.Item>
          </List>
        </p>

        <Header as="h3" content="Grading" />
        <p>
          You can earn up to 13 points for the assignment along the following four dimensions: idea, execution, evidence and
          related work. Refer to the following rubric for more specific information:
        </p>

        <Table definition>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Insufficient</Table.HeaderCell>
              <Table.HeaderCell>Adequate</Table.HeaderCell>
              <Table.HeaderCell>Proficient</Table.HeaderCell>
              <Table.HeaderCell>Mastery</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>Research idea (4 points)</Table.Cell>
              <Table.Cell><b>1</b>: Unclear or indistinguishable from standard application development.</Table.Cell>
              <Table.Cell><b>2</b>: Vague, only of tangential relevance to the usable privacy and security research community, or incremental in nature.</Table.Cell>
              <Table.Cell><b>3</b>: A clear research question or goal, but not cleanly articulated as a novel contribution beyond prior work or is not a major contribution.</Table.Cell>
              <Table.Cell><b>4</b>: A clear research question or goal that makes a significant addition to usable privacy and security research.</Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Execution (4 points)</Table.Cell>
              <Table.Cell><b>1</b>: The project has some complete components, but critical aspects are incomplete.</Table.Cell>
              <Table.Cell><b>2</b>: The basic elements of the research are complete, but either they are flawed or important aspects are still missing.</Table.Cell>
              <Table.Cell><b>3</b>: All relevant aspects of the project have been completed, but they have flaws.</Table.Cell>
              <Table.Cell><b>4</b>: The research question has been explored and answered thoroughly through the project and writeup.</Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Evidence (3 points)</Table.Cell>
              <Table.Cell><b>1</b>: The evaluation or study is incomplete or has major analysis flaws.</Table.Cell>
              <Table.Cell><b>2</b>: The evaluation or the study is complete, but has significant analysis flaws. More leniency is applied to systems projects on this rubric item.</Table.Cell>
              <Table.Cell colSpan={2}><b>3</b>: The evaluation or the study is complete and convincingly argues for a result.</Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Related work (2 points)</Table.Cell>
              <Table.Cell colSpan={2}><b>1</b>: The description of related work is incomplete or surface-level.</Table.Cell>
              <Table.Cell colSpan={2}><b>2</b>: The writeup covers major points of related work, and explains how this research extends them.</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/pages/group-project-assignments/final-paper-page.js